import { useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import {
  AugmentedCreateLifetimeExclusionEventInput,
  AugmentedUpdateLifetimeExclusionEventInput,
  DocumentType,
} from '@/types/schema';

import {
  useCreateLifetimeExclusionEventMutation,
  useDeleteLifetimeExclusionEventMutation,
  useUpdateLifetimeExclusionEventMutation,
} from '../graphql/LifetimeExclusionEvents.generated';
import { defaultValues } from './RecordExemptionUsedModal.constants';
import {
  RecordExemptionUsedModalForm,
  useFormOptions,
} from './RecordExemptionUsedModal.form';
import { RecordExemptionUsedForm } from './RecordExemptionUsedModal.types';
import {
  getCreateLifetimeExclusionEventInput,
  getUpdateLifetimeExclusionEventInput,
  useSyncDataToForm,
} from './RecordExemptionUsedModal.utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  householdId: string;
  lifetimeExclusionEventId?: string;
  isDuplicate?: boolean;
  isEdit?: boolean;
}

export function RecordExemptionUsed({
  isOpen,
  onClose,
  householdId,
  lifetimeExclusionEventId,
  isDuplicate,
  isEdit,
}: Props) {
  const { showFeedback, createErrorFeedback } = useFeedback();
  const { reportError } = useReportError();
  const {
    uploadDocuments,
    setSelectedExistingDocuments,
    selectedExistingDocuments,
    clearUploadedFiles,
  } = useMultiDocumentUploaderContext();
  const { formRef, handleSave } = useFormSaveHandler();
  const canEdit = useCanEditHouseholdData();

  const { primaryClientOptions, hasCtResident } = useFormOptions({
    householdId,
  });

  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<RecordExemptionUsedForm>();

  const { loading: loadingSyncDataToForm, originalDocumentIds } =
    useSyncDataToForm({
      lifetimeExclusionEventId,
      isDuplicate,
      isOpen,
    });

  // Because `originalDocumentIds` is a mutable ref whose identity never
  // changes, we read its `.current` value into a local variable so React can
  // detect changes when the array reference is updated inside the utils
  // hook.
  const originalDocumentIdsArray = originalDocumentIds.current;

  useEffect(() => {
    if (originalDocumentIdsArray.length > 0) {
      setSelectedExistingDocuments(
        originalDocumentIdsArray.map((id) => ({
          id,
          displayName: '', // we don't use them in this context
        }))
      );
    }
  }, [originalDocumentIdsArray, setSelectedExistingDocuments]);

  const [createLifetimeExclusionEvent] =
    useCreateLifetimeExclusionEventMutation({
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      onError: (error) => {
        createErrorFeedback('Error recording exemption, please try again')();
        reportError('could not create lifetime exclusion event', error, {
          householdId,
        });
      },
      onCompleted: () => {
        showFeedback('Additional exemption recorded successfully', {
          variant: 'success',
        });
      },
    });

  const [updateLifetimeExclusionEvent] =
    useUpdateLifetimeExclusionEventMutation({
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback('Error recording exemption, please try again');
        reportError('could not update lifetime exclusion event', error, {
          householdId,
        });
      },
      onCompleted: () => {
        showFeedback('Additional exemption recorded successfully', {
          variant: 'success',
        });
      },
    });

  const [deleteLifetimeExclusionEvent, { loading: isDeleting }] =
    useDeleteLifetimeExclusionEventMutation({
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback('Error deleting exemption, please try again');
        reportError('could not delete lifetime exclusion event', error, {
          householdId,
        });
      },
      onCompleted: () => {
        showFeedback('Exemption deleted successfully', {
          variant: 'success',
        });
      },
    });

  const onValidSubmission: SubmitHandler<RecordExemptionUsedForm> = async (
    values
  ) => {
    try {
      // Upload documents first and get their IDs
      const uploadedDocumentIds = await uploadDocuments({
        householdId,
        defaultDocumentType: DocumentType.GiftTaxReturn,
      });

      // Use the current selected documents from context instead of form values
      const existingDocumentIds = selectedExistingDocuments.map(
        (doc) => doc.id
      );
      const allDocumentIds = [...existingDocumentIds, ...uploadedDocumentIds];
      const updatedValues = {
        ...values,
        recordExemptionUsedModal: {
          ...values.recordExemptionUsedModal,
          documentIds: allDocumentIds,
        },
      };

      if (isEdit) {
        const updateLifetimeExclusionEventInput: AugmentedUpdateLifetimeExclusionEventInput =
          getUpdateLifetimeExclusionEventInput({
            form: updatedValues,
            lifetimeExclusionEventId,
            originalDocumentIds,
          });

        // submit update form
        return updateLifetimeExclusionEvent({
          variables: {
            input: updateLifetimeExclusionEventInput,
          },
        });
      } else {
        const createLifetimeExclusionEventInput: AugmentedCreateLifetimeExclusionEventInput =
          getCreateLifetimeExclusionEventInput(updatedValues, householdId);

        // submit create form
        return createLifetimeExclusionEvent({
          variables: {
            input: createLifetimeExclusionEventInput,
          },
        });
      }
    } catch (error) {
      reportError('Failed to upload documents', error as Error);
      showFeedback('Failed to upload documents. Please try again.');
      throw error;
    }
  };

  const submitHandler = handleSubmit(onValidSubmission);

  const closeModal = useCallback(() => {
    reset();
    clearUploadedFiles();
    onClose();
  }, [clearUploadedFiles, onClose, reset]);

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  const onDelete = useCallback(async () => {
    await deleteLifetimeExclusionEvent({
      variables: {
        deleteLifetimeExclusionEventId: lifetimeExclusionEventId ?? '',
      },
    });

    closeModal();
  }, [closeModal, deleteLifetimeExclusionEvent, lifetimeExclusionEventId]);

  let submitButtonText = 'Record exemption';

  if (isEdit) {
    submitButtonText = 'Save changes';
  }

  if (isDuplicate) {
    submitButtonText = 'Duplicate addition';
  }

  const actions = (
    <FormModalActions.Provider
      formState={formState}
      isDeleting={isDeleting}
      disabled={loadingSyncDataToForm}
      viewOnly={!canEdit}
    >
      {isEdit && <FormModalActions.DeleteButton onConfirmDelete={onDelete} />}
      <FormModalActions.CancelButton onClick={closeModal} />
      <FormModalActions.SaveButton onClick={handleSave}>
        {submitButtonText}
      </FormModalActions.SaveButton>
    </FormModalActions.Provider>
  );

  const heading = useMemo(() => {
    if (isEdit) {
      return 'Edit additional lifetime exemption';
    }
    if (isDuplicate) {
      return 'Duplicate additional lifetime';
    }
    return 'Record additional exemption used';
  }, [isDuplicate, isEdit]);

  return (
    <FormModal
      isOpen={isOpen}
      heading={heading}
      onClose={closeModal}
      actions={actions}
      shouldBlockClose={shouldBlockNavigation}
    >
      {loadingSyncDataToForm ? (
        <Loader
          boxProps={{
            sx: {
              textAlign: 'center',
              my: 3,
            },
          }}
        />
      ) : (
        <HouseholdPermissionAwareDisabledProvider
          forceDisabled={loadingSyncDataToForm}
        >
          <form ref={formRef} onSubmit={submitHandler} noValidate>
            <RecordExemptionUsedModalForm
              primaryClientOptions={primaryClientOptions}
              hasCtResident={hasCtResident}
              householdId={householdId}
            />
          </form>
        </HouseholdPermissionAwareDisabledProvider>
      )}
    </FormModal>
  );
}

export function RecordExemptionUsedModal({ ...props }: Props) {
  const formMethods = useForm<RecordExemptionUsedForm>({ defaultValues });

  return (
    <MultiDocumentUploaderProvider>
      <FormProvider {...formMethods}>
        <RecordExemptionUsed {...props} />
      </FormProvider>
    </MultiDocumentUploaderProvider>
  );
}
